import React, { useContext, useState } from "react";

import { Organisation, User, UserStatus } from "@/api/SolarCloudApi";
import { Message } from "@/components/Messages";
import { Authentication, useAuthenticationQuery } from "@/util/authentication";
import { logger } from "@/util/logger";
import {
  Feature,
  hasFeature,
  useEnvironmentContext,
} from "@/contexts/EnvironmentContext";

export type MatchFunction = (
  name: string,
  needsUser?: boolean,
  status?: UserStatus | Array<UserStatus>,
  feature?: Feature | Array<Feature>
) => boolean;

export type Profile = {
  authentication?: Authentication;
  isAuthenticationLoading?: boolean;
  organisation?: Organisation;
  setOrganisation: (organisation?: Organisation) => Promise<void> | void;
  email?: string;
  setEmail: (email?: string) => Promise<void> | void;
  country?: string;
  setCountry: (country?: string) => Promise<void> | void;
  messages: Message[];
  setMessages: (messages: Message[]) => void;
  match: MatchFunction;
};

const uninitialised = () => {
  logger.log("ERROR - Attempted to update an unitialised profile");
};
const uninitialisedMatch: MatchFunction = (
  name,
  needsUser?,
  status?,
  feature?
) => {
  logger.error("ERROR - Attempted to match with an uninitiailsed function");
  return false;
};

const ProfileContext = React.createContext<Profile>({
  // setUser: uninitialised,
  // setFederatedUser: uninitialised,
  setOrganisation: uninitialised,
  // setUserid: uninitialised,
  setEmail: uninitialised,
  setCountry: uninitialised,
  // setCountryData: uninitialised,
  // setOrder: uninitialised,
  // setCatalog: uninitialised,
  // setTotals: uninitialised,
  // setTrackerEvents: uninitialised,
  // setPowerGenerationInfo: uninitialised,
  // informationUpdated: uninitialised,
  messages: [],
  setMessages: uninitialised,
  match: uninitialisedMatch,
});

export default function ProfileContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const environment = useEnvironmentContext();
  const { data: authentication, isLoading: isAuthenticationLoading } =
    useAuthenticationQuery();
  const [organisation, setOrganisation] = useState<Organisation | undefined>(
    undefined
  );
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [messages, setMessages] = useState<Message[]>([]);
  const user = authentication?.user;

  const profile: Profile = {
    authentication,
    isAuthenticationLoading,
    organisation,
    setOrganisation,
    email,
    setEmail,
    country,
    setCountry,
    messages,
    setMessages,
    get match() {
      return (
        name: string,
        needsUser?: boolean,
        status?: UserStatus | Array<UserStatus>,
        feature?: Feature | Array<Feature>
      ) => match(user, environment?.features, name, needsUser, status, feature);
    },
  };

  return (
    <ProfileContext.Provider value={profile}>
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfileContext = () => {
  const profile = useContext(ProfileContext);
  if (!profile) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return profile;
};

// export default ProfileContext;
const match = (
  user: User | undefined,
  features: Feature[] | undefined,
  name: string,
  needsUser?: boolean,
  status?: UserStatus | Array<UserStatus>,
  feature?: Feature | Array<Feature>
): boolean => {
  const trace = false;

  if (status) {
    if (!user?.status) {
      trace &&
        logger.log(
          "@@@@ match - no user status: ",
          "user:",
          user,
          "features:",
          features,
          "name:",
          name,
          "needsUser:",
          needsUser,
          "status:",
          status,
          "feature:",
          feature
        );
      return false;
    } else {
      const statuses = Array.isArray(status) ? status : [status];

      if (!statuses.includes(user.status)) {
        trace &&
          logger.log(
            "@@@@ match - user status not matched: ",
            "user:",
            user,
            "features:",
            features,
            "name:",
            name,
            "needsUser:",
            needsUser,
            "status:",
            status,
            "feature:",
            feature
          );
        return false;
      }
    }
  }

  if (!hasFeature(features, feature)) {
    trace &&
      logger.log(
        "@@@@ match - no feature: ",
        "user:",
        user,
        "features:",
        features,
        "name:",
        name,
        "needsUser:",
        needsUser,
        "status:",
        status,
        "feature:",
        feature
      );
    return false;
  }

  if (needsUser === true && !user) {
    trace &&
      logger.log(
        "@@@@ match - user expected: ",
        "user:",
        user,
        "features:",
        features,
        "name:",
        name,
        "needsUser:",
        needsUser,
        "status:",
        status,
        "feature:",
        feature
      );
    return false;
  }

  if (needsUser === false && user) {
    trace &&
      logger.log(
        "@@@@ match - no user expected: ",
        "user:",
        user,
        "features:",
        features,
        "name:",
        name,
        "needsUser:",
        needsUser,
        "status:",
        status,
        "featue:",
        feature
      );
    return false;
  }

  trace &&
    logger.log(
      "@@@@ match - matched: ",
      "user:",
      user,
      "features:",
      features,
      "name:",
      name,
      "needsUser:",
      needsUser,
      "status:",
      status,
      "featue:",
      feature
    );
  return true;
};
